import { useEffect, useState } from "react";
import Header from "../components/Header";
import VoiceLibrary from "../components/VoiceLibrary";
import api from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Trash2, Plus, Star, Download } from "lucide-react";
import { presetsForType, findPreset, PROVIDER_PRESETS } from "../data/providerPresets";
import { useConfig } from "../lib/config";

const KINDS = [
  { v:"emergent", l:"Emergent Universal (built-in)" },
  { v:"openai_compatible", l:"OpenAI-compatible endpoint (BYOK)" },
  { v:"pravaani", l:"Pravaani on RunPod (STT/TTS)" },
  { v:"muapi", l:"Muapi — MiniMax TTS & voice cloning" },
];

// Kinds whose credentials are entered per-provider rather than read from the
// server environment.
const BYOK_KINDS = ["openai_compatible", "pravaani", "muapi"];

// Kinds that serve exactly one pipeline step. Mirrors SINGLE_TYPE_KINDS in
// backend/models.py, which rejects a mismatch server-side; blocking it here
// just saves the round trip.
const SINGLE_TYPE_KINDS = { muapi: "tts", edge: "tts", elevenlabs: "tts", faster_whisper: "stt" };
const MUAPI_TTS_MODELS = ["minimax-speech-2.6-hd", "minimax-speech-2.6-turbo"];

export default function Settings() {
  const { featureEnabled } = useConfig();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({name:"",type:"llm",kind:"openai_compatible",base_url:"",api_key:"",model:"",is_default:false});
  const [saving, setSaving] = useState(false);
  const [presetId, setPresetId] = useState("");
  // Which fields hold preset-supplied values rather than typed ones. Without
  // this, "don't clobber the user's edits" cannot tell a value the user chose
  // from one the *previous* preset left behind -- so picking OpenAI then Groq
  // saved a provider named "OpenAI" running gpt-4o against Groq's endpoint.
  const [autoFilled, setAutoFilled] = useState({ name: false, model: false });
  const [fetchedModels, setFetchedModels] = useState(null);
  const [probing, setProbing] = useState(false);

  // Same two-switch rule as the project wizard: Pravaani ships independently
  // of the generic voice_cloning flag, so gating on that alone would hide the
  // one provider this library can enrol into.
  const cloningAvailable = featureEnabled("voice_cloning") || featureEnabled("voice_cloning_pravaani");

  const visibleKinds = KINDS.filter((k) =>
    (k.v !== "pravaani" || featureEnabled("byok_pravaani")) &&
    (k.v !== "muapi" || featureEnabled("byok_muapi"))
  );
  const preset = findPreset(presetId);
  // Presets are filtered by pipeline step, but the *selected* one must stay in
  // the list even after the user switches type -- otherwise the select renders
  // blank while presetId is still set, and the form silently disagrees with
  // what is on screen.
  const presetChoices = (() => {
    const fit = presetsForType(form.type);
    return preset && !fit.includes(preset) ? [...fit, preset] : fit;
  })();
  // Suggestions come from the live endpoint once probed, else the catalog.
  // Either way the input stays free text -- a datalist suggests, it never
  // restricts, so a model we have never heard of is still typeable.
  const modelOptions = form.kind === "muapi"
    ? MUAPI_TTS_MODELS
    : (fetchedModels ?? (preset?.types?.[form.type] || []));

  const applyPreset = (id) => {
    setPresetId(id);
    setFetchedModels(null);
    const pr = findPreset(id);
    if (!pr) return;
    const suggested = (pr.types?.[form.type] || [])[0] || "";
    setForm((f) => ({
      ...f,
      kind: "openai_compatible",
      base_url: pr.baseUrl,
      // Overwrite our own earlier suggestions; never overwrite typed input.
      name: (!f.name || autoFilled.name) ? pr.label : f.name,
      model: (!f.model || autoFilled.model) ? suggested : f.model,
    }));
    setAutoFilled({
      name: !form.name || autoFilled.name,
      model: (!form.model || autoFilled.model) && !!suggested,
    });
  };

  // Changing the pipeline step invalidates a suggested model: gpt-4o is not a
  // TTS voice. Re-derive it, but only if it was ours to change.
  const changeType = (t) => {
    const suggested = (preset?.types?.[t] || [])[0] || "";
    setFetchedModels(null);
    setForm((f) => ({
      ...f,
      type: t,
      // Clear rather than keep when the preset serves no model for the new
      // type. Keeping it is how an NVIDIA NIM *chat* model got saved as a TTS
      // provider: the render then 404'd on /audio/speech, because that endpoint
      // has no speech API at all.
      model: autoFilled.model ? suggested : f.model,
    }));
  };

  // A preset that does not serve the chosen step cannot produce a working
  // provider, so this blocks the save rather than merely warning. The warning
  // alone was not enough -- it was shown, and the provider was saved anyway.
  const presetTypeMismatch = !!preset && !Array.isArray(preset.types?.[form.type]);
  const kindOnly = SINGLE_TYPE_KINDS[form.kind];
  const kindTypeMismatch = !!kindOnly && form.type !== kindOnly;

  // Choosing a single-purpose kind snaps the type to the one it serves and
  // offers its default model, so the common path needs no second thought.
  const changeKind = (k) => {
    setForm((f) => {
      const only = SINGLE_TYPE_KINDS[k];
      const next = { ...f, kind: k, ...(only ? { type: only } : {}) };
      if (k === "muapi" && (!f.model || autoFilled.model)) next.model = MUAPI_TTS_MODELS[0];
      // A preset's URL belongs to that vendor. Kept, it was saved on a
      // Pravaani provider and every transcription went to api.openai.com.
      if (k !== "openai_compatible" && PROVIDER_PRESETS.some((pr) => pr.baseUrl === f.base_url)) next.base_url = "";
      return next;
    });
    if (k === "muapi") setAutoFilled((a) => ({ ...a, model: true }));
    // Presets only describe OpenAI-compatible endpoints. One left selected
    // from before would keep its mismatch check alive and block the save.
    if (k !== "openai_compatible") { setPresetId(""); setFetchedModels(null); }
  };

  const probeModels = async () => {
    if (!form.base_url) { toast.error("Enter a base URL first"); return; }
    setProbing(true);
    try {
      const r = await api.post("/providers/probe-models", {
        base_url: form.base_url, api_key: form.api_key || null,
      });
      const list = r.data?.models || [];
      setFetchedModels(list);
      toast.success(list.length ? `Found ${list.length} model${list.length===1?"":"s"}` : "Endpoint returned no models");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach that endpoint");
    } finally { setProbing(false); }
  };

  const load = async () => { const r = await api.get("/providers"); setItems(r.data); };
  useEffect(()=>{ load(); },[]);

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/providers", form);
      toast.success("Provider added");
      setForm({name:"",type:"llm",kind:"openai_compatible",base_url:"",api_key:"",model:"",is_default:false});
      setPresetId(""); setFetchedModels(null); setAutoFilled({name:false, model:false});
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/providers/${id}`); load(); };
  const setDefault = async (id) => { await api.patch(`/providers/${id}/default`); load(); };

  return (
    <div className="min-h-screen">
      <Header/>
      <main id="main" tabIndex={-1} className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <NoticeRegion />
        <div className="marquee-tag mb-2">// PROVIDERS</div>
        <h1 className="font-display font-black type-title mb-3 tracking-tighter">BRING YOUR OWN.</h1>
        <p className="text-ink-muted max-w-2xl mb-8">Point each pipeline step at any OpenAI-compatible endpoint — Groq, NVIDIA NIM, ElevenLabs, self-hosted vLLM/llama.cpp, or the built-in Emergent Universal Key.</p>

        <form onSubmit={save} className="brutal-card mb-10 space-y-4" data-testid="provider-form">
          <h2 className="marquee-tag">// ADD PROVIDER</h2>
          {form.kind === "openai_compatible" && (
            <div>
              <label className="brutal-label" htmlFor="provider-preset">Preset</label>
              <select id="provider-preset" data-testid="provider-preset" className="brutal-select"
                value={presetId} onChange={(e)=>applyPreset(e.target.value)}>
                <option value="">Custom — I'll enter the URL myself</option>
                {presetChoices.map(pr=>(
                  <option key={pr.id} value={pr.id}>
                    {pr.label}{Array.isArray(pr.types?.[form.type]) ? "" : ` — no ${form.type.toUpperCase()}`}
                  </option>
                ))}
              </select>
              {preset?.note && <p className="text-xs text-ink-muted mt-1">{preset.note}</p>}
              {preset?.selfHosted && (
                <p className="text-xs mt-1 text-state-warning-ink" data-testid="preset-selfhosted-warning">
                  Endpoints on a private address are blocked by default. Add this host to
                  {" "}<code>PROVIDER_URL_ALLOWED_HOSTS</code> on the server, or point this at a
                  public hostname, or saving will fail.
                </p>
              )}
              {presetTypeMismatch && (
                <p className="text-xs mt-1 text-state-danger" role="alert" data-testid="preset-type-mismatch">
                  {preset?.label} does not provide a {form.type.toUpperCase()} endpoint — saving is
                  blocked because this provider would fail at render time. Choose a different
                  preset, or pick Custom and enter a base URL that implements it.
                </p>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="brutal-label" htmlFor="provider-name">Name</label>
              <input id="provider-name" required data-testid="provider-name" className="brutal-input" value={form.name} onChange={(e)=>{upd("name",e.target.value); setAutoFilled(a=>({...a,name:false}));}} placeholder="e.g. Groq Llama 3.1"/>
            </div>
            <div>
              <label className="brutal-label" htmlFor="provider-type">Type</label>
              <select id="provider-type" data-testid="provider-type" className="brutal-select" value={form.type} onChange={(e)=>changeType(e.target.value)}>
                <option value="llm">LLM (text)</option>
                <option value="tts">TTS (voice)</option>
                <option value="stt">STT (transcription)</option>
              </select>
            </div>
            <div>
              <label className="brutal-label" htmlFor="provider-kind">Kind</label>
              <select id="provider-kind" data-testid="provider-kind" className="brutal-select" value={form.kind} onChange={(e)=>changeKind(e.target.value)}>
                {visibleKinds.map(k=><option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
            </div>
            <div>
              <label className="brutal-label" htmlFor="provider-model">{form.kind === "pravaani" ? "RunPod endpoint ID" : "Model"}</label>
              <div className="flex gap-2">
                <input id="provider-model" required data-testid="provider-model" className="brutal-input flex-1"
                  list={modelOptions.length ? "provider-model-options" : undefined}
                  value={form.model} onChange={(e)=>{upd("model",e.target.value); setAutoFilled(a=>({...a,model:false}));}}
                  placeholder={form.kind === "pravaani" ? "e.g. a1b2c3d4e5f6g7" : "e.g. llama-3.3-70b-versatile"}/>
                {form.kind === "openai_compatible" && (
                  <button type="button" onClick={probeModels} disabled={probing || !form.base_url}
                    className="brutal-btn brutal-btn-secondary shrink-0" data-testid="provider-fetch-models"
                    title="Ask this endpoint which models it serves">
                    <Download size={14}/> {probing ? "…" : "Fetch"}
                  </button>
                )}
              </div>
              {modelOptions.length > 0 && (
                <datalist id="provider-model-options">
                  {modelOptions.map(m=><option key={m} value={m}/>)}
                </datalist>
              )}
              {fetchedModels !== null ? (
                <p className="text-xs text-ink-muted mt-1" data-testid="provider-model-hint">
                  {fetchedModels.length} model{fetchedModels.length===1?"":"s"} live from this endpoint.
                </p>
              ) : modelOptions.length > 0 && (
                <p className="text-xs text-ink-muted mt-1" data-testid="provider-model-hint">
                  Suggestions only — vendors retire models often. Use Fetch for the current list.
                </p>
              )}
              {form.kind === "muapi" && (
                <p className="text-xs text-ink-muted mt-1" data-testid="muapi-hint">
                  MiniMax speech through the Muapi gateway. This provider also lets you clone a voice
                  on the project page. Muapi has no speech-to-text, so it is TTS-only.
                </p>
              )}
              {form.kind === "pravaani" && (
                <p className="text-xs text-ink-muted mt-1">
                  Register one provider per endpoint — set Type to STT for your ASR endpoint and TTS for your speech endpoint.
                </p>
              )}
            </div>
            {BYOK_KINDS.includes(form.kind) && (
              <>
                <div>
                  <label className="brutal-label" htmlFor="provider-base-url">Base URL{(form.kind === "pravaani" || form.kind === "muapi") ? " (optional)" : ""}</label>
                  <input id="provider-base-url" data-testid="provider-base-url" className="brutal-input" value={form.base_url} onChange={(e)=>upd("base_url",e.target.value)}
                    placeholder={form.kind === "pravaani" ? "https://api.runpod.ai (default)" : form.kind === "muapi" ? "https://api.muapi.ai (default)" : (preset?.baseUrl || "https://api.groq.com/openai/v1")}/>
                </div>
                <div>
                  <label className="brutal-label" htmlFor="provider-api-key">API Key</label>
                  <input id="provider-api-key" data-testid="provider-api-key" type="password" className="brutal-input" value={form.api_key} onChange={(e)=>upd("api_key",e.target.value)}
                    placeholder={form.kind === "pravaani" ? "RunPod API key (rpa_...)" : form.kind === "muapi" ? "Muapi API key" : (preset?.keyHint || "sk-...")}/>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input id="provider-default" type="checkbox" checked={form.is_default} onChange={(e)=>upd("is_default",e.target.checked)} data-testid="provider-default"/>
            <label className="text-sm" htmlFor="provider-default">Set as default for this type</label>
          </div>
          {kindTypeMismatch && (
            <p className="text-xs text-state-danger" role="alert" data-testid="kind-type-mismatch">
              A {KINDS.find((k) => k.v === form.kind)?.l || form.kind} provider can only be used for{" "}
              {kindOnly.toUpperCase()} — set Type to {kindOnly.toUpperCase()} to save it.
            </p>
          )}
          <button disabled={saving || presetTypeMismatch || kindTypeMismatch} type="submit" className="brutal-btn" data-testid="provider-save"><Plus size={14}/> {saving?"Saving…":"Add Provider"}</button>
        </form>

        <h2 className="marquee-tag mb-4">// YOUR PROVIDERS</h2>
        {items.length === 0 ? (
          <div className="brutal-card text-center py-10 text-ink-muted" data-testid="providers-empty">
            No providers yet. Emergent Universal Key is used by default.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="providers-list">
            {items.map(p => (
              <div key={p.provider_id} className="brutal-card flex items-center justify-between" data-testid={`provider-${p.provider_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold type-subheading">{p.name}</h3>
                    {p.is_default && <span className="brutal-chip bg-state-success text-white border-rule">DEFAULT</span>}
                    <span className="brutal-chip">{p.type.toUpperCase()}</span>
                    <span className="brutal-chip">{p.kind}</span>
                  </div>
                  <div className="text-xs text-ink-muted mt-1">Model: {p.model}{p.base_url ? ` · ${p.base_url}` : ""}</div>
                </div>
                <div className="flex gap-2">
                  {!p.is_default && <button onClick={()=>setDefault(p.provider_id)} className="brutal-btn brutal-btn-secondary" data-testid={`btn-default-${p.provider_id}`} aria-label={`Set ${p.name || p.kind} as default`}><Star size={14}/></button>}
                  <button onClick={()=>del(p.provider_id)} className="brutal-btn brutal-btn-secondary" data-testid={`btn-delete-${p.provider_id}`} aria-label={`Delete provider ${p.name || p.kind}`}><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {cloningAvailable && <VoiceLibrary />}
      </main>
    </div>
  );
}
